import { Fragment, useMemo, useState } from "react"
import { useQuery } from "@rocicorp/zero/react"
import { Link } from "@tanstack/react-router"
import { formatDistanceToNow } from "date-fns"
import {
	IconPen2Outline18 as Edit2Icon,
	IconFolderPlusOutline18 as FolderPlus,
	IconMagnifierOutline18 as MagnifierIcon,
	IconPlusOutline18 as Plus,
	IconTagPlusOutline18 as TagPlus,
	IconTrashOutline18 as TrashIcon,
} from "nucleo-ui-outline-18"

import type { ProjectIconKeys } from "@productlane/ui"
import type { Tag, TagGroup } from "@productlane/zero"
import { cn } from "@productlane/lib/src/browser"
import {
	AlertDialog,
	Button,
	ContextMenu,
	ContextMenuContent,
	ContextMenuItem,
	ContextMenuTrigger,
	Empty,
	IconButton,
	Input,
	SortableTableHeader,
} from "@productlane/ui"
import {
	DropdownMenu,
	DropdownMenuItem,
} from "@productlane/ui/src/components/internal/dropdown"
import { EllipsisVerticalIcon as MoreVertical } from "@productlane/ui/src/icons/project/ellipsis-icon"
import { mutators } from "@productlane/zero/mutators"
import { queries } from "@productlane/zero/queries"

import type { TagSortType } from "./sort-tags"
import EmptyProject from "@/assets/empty-states/empty-project.png"
import { MetaInfo } from "@/components/meta-info"
import { RoleCheck } from "@/components/shared/role-check"
import { analytics } from "@/lib/analytics"
import { api } from "@/lib/api"
import { useSetBackNavigation } from "@/lib/back-navigation"
import { getProjectIcon } from "@/lib/projects"
import { isQuerySettled } from "@/lib/query-settled"
import { useRole } from "@/lib/useRole"
import { mutateOk, useZero } from "@/lib/zero"
import { SettingsHeader } from "../../settings/settings-header"
import { CreateTagGroupDialog } from "../tagGroups/create-tag-group-dialog"
import { UpdateTagGroupDialog } from "../tagGroups/update-tag-group-dialog"
import { CreateTagDialog } from "./create-tag-dialog"
import { DEFAULT_TAG_SORT, filterTags, sortTags } from "./sort-tags"
import { UpdateTagDialog } from "./update-tag-dialog"

// Rows pick the page gutter back up as their own padding, so a hover runs edge
// to edge while the text still lines up with the title above it.
const BLEED = "-mx-5 sm:-mx-8"
const GUTTER = "px-5 sm:px-8"
const GRID = cn(
	"grid grid-cols-[minmax(0,1fr)_90px_130px_110px] items-center gap-4",
	GUTTER,
)

const COLUMNS: Array<{
	key: string
	label: string
	asc: TagSortType
	desc: TagSortType
}> = [
	{ key: "name", label: "Name", asc: "name-asc", desc: "name-desc" },
	{
		key: "threads",
		label: "Threads",
		asc: "threads-asc",
		desc: "threads-desc",
	},
	{
		key: "lastUsed",
		label: "Last used",
		asc: "lastUsed-asc",
		desc: "lastUsed-desc",
	},
	{
		key: "created",
		label: "Created",
		asc: "created-asc",
		desc: "created-desc",
	},
]

const relative = (iso: string | number | null) =>
	iso === null ? "" : formatDistanceToNow(new Date(iso), { addSuffix: true })

const created = (createdAt: number) =>
	new Date(createdAt).toLocaleDateString(undefined, {
		month: "short",
		year: "numeric",
	})

const tagsQueries = () => ({
	tagGroups: queries.tagGroups(),
	tags: queries.tags(),
})

export const tagsPagePreloadQueries = () => {
	const q = tagsQueries()
	return [q.tagGroups, q.tags]
}

export const TagsScreen = () => {
	useSetBackNavigation({ type: "opportunities", url: "/tags" })
	const zero = useZero()

	const q = tagsQueries()
	const [tagGroups, tagGroupsResult] = useQuery(q.tagGroups)
	const [tags] = useQuery(q.tags)

	const statsQuery = api.tag.threadStats.useQuery(undefined, {
		staleTime: 60_000,
	})
	const stats = useMemo(() => statsQuery.data ?? {}, [statsQuery.data])
	const hasStats = statsQuery.data !== undefined

	const [filter, setFilter] = useState("")
	const [sortType, setSortType] = useState<TagSortType>(DEFAULT_TAG_SORT)

	const [isCreateTagGroupOpen, setIsCreateTagGroupOpen] = useState(false)
	const [isUpdateTagGroupOpen, setIsUpdateTagGroupOpen] = useState(false)
	const [updateTagGroup, setUpdateTagGroup] = useState<TagGroup | null>(null)
	const [deleteTagGroupId, setDeleteTagGroupId] = useState("")
	const [isDialogOpen, setDialogOpen] = useState(false)
	const [openTagGroupId, setOpenTagGroupId] = useState("")
	const [editTag, setEditTag] = useState<Tag | null>(null)
	const [deleteTagId, setDeleteTagId] = useState("")
	const { role } = useRole()
	const canEditTags = !!role && role !== "VIEWER"

	const rows = useMemo(() => {
		return tagGroups
			.map((tagGroup) => {
				const groupTags = sortTags(
					filterTags(
						tags.filter((tag) => tag.tagGroupId === tagGroup.id),
						filter,
					),
					sortType,
					stats,
				)
				return { tagGroup, tags: groupTags }
			})
			.filter((row) => row.tags.length > 0)
	}, [tagGroups, tags, filter, sortType, stats])

	return (
		<>
			<MetaInfo title="Tags" />
			<SettingsHeader
				title="Tags"
				subtitle="Create and organize tags to analyze support volume."
				action={
					<RoleCheck not={["VIEWER"]}>
						<div className="flex flex-wrap items-center gap-3">
							<Button
								variant="outline"
								Icon={TagPlus}
								onClick={() => {
									setOpenTagGroupId(tagGroups[0]?.id ?? "")
									setDialogOpen(true)
								}}
							>
								New tag
							</Button>
							<Button
								variant="outline"
								Icon={FolderPlus}
								onClick={() => {
									setIsCreateTagGroupOpen(true)
									analytics.track("open-add-outcome-dialog")
								}}
							>
								New group
							</Button>
						</div>
					</RoleCheck>
				}
			/>
			<CreateTagDialog
				isOpen={isDialogOpen}
				defaultTagGroupId={openTagGroupId}
				onOpenChange={(value) => {
					if (value) {
						analytics.track("open-create-opportunity-dialog", {
							tagGroupId: openTagGroupId,
						})
					}
					if (!value) {
						setTimeout(() => {
							setOpenTagGroupId("")
						}, 150)
						setDialogOpen(false)
					}
				}}
			/>
			<CreateTagGroupDialog
				isOpen={isCreateTagGroupOpen}
				onOpenChange={(value) => {
					setIsCreateTagGroupOpen(value)
					if (value) {
						analytics.track("open-create-outcome-dialog")
					}
				}}
			/>
			<UpdateTagGroupDialog
				isOpen={isUpdateTagGroupOpen}
				onOpenChange={(value) => {
					if (value) {
						analytics.track("open-update-outcome-dialog", {
							id: updateTagGroup?.id,
						})
					}
					if (!value) {
						setIsUpdateTagGroupOpen(false)
						setTimeout(() => {
							setUpdateTagGroup(null)
						}, 150)
					}
				}}
				values={updateTagGroup}
			/>
			<AlertDialog
				isOpen={!!deleteTagGroupId}
				onOpenChange={(value) => {
					if (value) {
						analytics.track("open-delete-outcome-dialog")
					}
					if (!value) {
						setDeleteTagGroupId("")
					}
				}}
				confirmText="Delete"
				title="Are you sure?"
				description="Do you really want to delete this group and all tags?"
				onCancel={() => {
					setDeleteTagGroupId("")
					analytics.track("cancel-delete-outcome", { id: deleteTagGroupId })
				}}
				onConfirm={async () => {
					await mutateOk(
						zero.mutate(mutators.tagGroups.delete({ id: deleteTagGroupId })),
					)
					setDeleteTagGroupId("")
					analytics.track("delete-tag-group", { id: deleteTagGroupId })
				}}
			/>
			{editTag && (
				<UpdateTagDialog
					isOpen
					onOpenChange={(value) => {
						if (!value) setEditTag(null)
					}}
					values={{
						id: editTag.id,
						icon: editTag.icon as ProjectIconKeys,
						name: editTag.name,
						tagGroupId: editTag.tagGroupId,
					}}
				/>
			)}
			<AlertDialog
				isOpen={!!deleteTagId}
				onOpenChange={(value) => {
					if (!value) setDeleteTagId("")
				}}
				confirmText="Delete"
				title="Are you sure?"
				description="Do you really want to delete this tag?"
				onCancel={() => setDeleteTagId("")}
				onConfirm={async () => {
					const id = deleteTagId
					setDeleteTagId("")
					await mutateOk(zero.mutate(mutators.tags.delete({ id })))
					analytics.track("delete-tag", { id })
				}}
			/>
			{tagGroups.length > 0 || !isQuerySettled(tagGroupsResult) ? (
				<div className={cn(BLEED, "pb-2 text-sm")}>
					<div className={cn("flex items-center gap-2 pb-4", GUTTER)}>
						<MagnifierIcon className="h-4 w-4 flex-none text-secondary" />
						<Input
							variant="borderless"
							textSize="sm"
							name="tag-filter"
							placeholder="Filter by name..."
							value={filter}
							className="px-0 placeholder:text-tertiary"
							inputClassName="h-7"
							onChange={(event) => setFilter(event.target.value)}
						/>
					</div>

					<div
						className={cn(
							GRID,
							"h-8 border-b border-gray-200 text-xs text-secondary dark:border-gray-700/80",
						)}
					>
						{COLUMNS.map((column, index) => (
							<SortableTableHeader
								key={column.key}
								label={column.label}
								currentSortType={sortType}
								sortTypeAsc={column.asc}
								sortTypeDesc={column.desc}
								onClick={setSortType}
								arrowPosition={index === 0 ? "right" : "left"}
							/>
						))}
					</div>

					{rows.map(({ tagGroup, tags: groupTags }, index) => (
						<Fragment key={tagGroup.id}>
							<div className={cn(GRID, "group h-10", index > 0 && "mt-5")}>
								<div className="flex min-w-0 items-center gap-2.5">
									<span className="flex h-4 w-4 flex-none items-center justify-center">
										<span
											className="h-2.5 w-2.5 rounded-[3px]"
											style={{ backgroundColor: tagGroup.color }}
										/>
									</span>
									<div className="flex min-w-0 items-baseline gap-2">
										<span className="truncate font-medium text-secondary">
											{tagGroup.name}
										</span>
										<span className="flex-none text-xs text-tertiary">
											{groupTags.length}
										</span>
									</div>
									<RoleCheck not={["VIEWER"]}>
										<span className="flex items-center opacity-0 group-hover:opacity-100">
											<IconButton
												Icon={Plus}
												variant="ghost"
												aria-label="New tag in group"
												onClick={() => {
													setOpenTagGroupId(tagGroup.id)
													setDialogOpen(true)
												}}
											/>
											<DropdownMenu
												contentProps={{ align: "end" }}
												trigger={
													<IconButton
														Icon={MoreVertical}
														variant="ghost"
														aria-label="Group actions"
														iconClassName="fill-current"
													/>
												}
											>
												<DropdownMenuItem
													Icon={Edit2Icon}
													onClick={() => {
														setUpdateTagGroup(tagGroup)
														setIsUpdateTagGroupOpen(true)
													}}
												>
													Edit
												</DropdownMenuItem>
												<DropdownMenuItem
													Icon={TrashIcon}
													onClick={() => {
														setDeleteTagGroupId(tagGroup.id)
													}}
												>
													Delete
												</DropdownMenuItem>
											</DropdownMenu>
										</span>
									</RoleCheck>
								</div>
								<span />
								<span />
								<span />
							</div>

							{groupTags.map((tag) => {
								const Icon = getProjectIcon(tag.icon)
								const stat = stats[tag.id]
								const row = (
									<Link
										to="/tags/$id"
										params={{ id: tag.id }}
										className={cn(
											GRID,
											"h-9 select-none hover:bg-gray-50 dark:hover:bg-gray-850",
										)}
									>
										<div className="flex min-w-0 items-center gap-2.5 pl-[1.625rem]">
											<Icon
												className="h-4 w-4 flex-none"
												style={{ color: tagGroup.color }}
											/>
											<span className="truncate text-primary">{tag.name}</span>
										</div>
										<span className="text-right tabular-nums text-secondary">
											{hasStats ? (stat?.threadCount ?? 0) : ""}
										</span>
										<span className="truncate text-right text-secondary">
											{hasStats ? relative(stat?.lastUsedAt ?? null) : ""}
										</span>
										<span className="text-right text-secondary">
											{created(tag.createdAt)}
										</span>
									</Link>
								)
								if (!canEditTags) return <Fragment key={tag.id}>{row}</Fragment>
								return (
									<ContextMenu key={tag.id}>
										<ContextMenuTrigger asChild>{row}</ContextMenuTrigger>
										<ContextMenuContent className="w-48">
											<ContextMenuItem
												Icon={Edit2Icon}
												onSelect={() => setEditTag(tag)}
											>
												Edit
											</ContextMenuItem>
											<ContextMenuItem
												Icon={TrashIcon}
												onSelect={() => setDeleteTagId(tag.id)}
											>
												Delete
											</ContextMenuItem>
										</ContextMenuContent>
									</ContextMenu>
								)
							})}
						</Fragment>
					))}
				</div>
			) : (
				<div className="flex justify-center py-12">
					<Empty
						variant="left"
						title="Tags"
						text="Discover which tags are trending."
						image={
							<img
								src={EmptyProject}
								alt="No tags"
								className="aspect-auto h-[65px]"
							/>
						}
						action={
							<div className="flex items-center gap-3">
								<RoleCheck not={["VIEWER"]}>
									<Button
										onClick={() => {
											setDialogOpen(true)
										}}
									>
										New tag
									</Button>
								</RoleCheck>
								<Button
									variant="outline"
									onClick={() => {
										// @ts-expect-error Global widget calls
										// eslint-disable-next-line
										window.Productlane.openDocs(
											"21068862-387e-4231-bddf-c15abae813ff",
										)
									}}
								>
									How it works
								</Button>
							</div>
						}
					/>
				</div>
			)}
		</>
	)
}
